<template>
  <div style="max-width: 100vw !important; margin:0 auto !important;">
    <data-filter :on-apply-filter="onApplyFilter" :minute=true />

    <template v-if="isLoading">
      <div class="text-center mt-5">
        <b-spinner label="Spinning"></b-spinner>
      </div>
    </template>
    <template v-else-if="currAssetSummary && !isLoading">
      <div class="row">
        <div class="col-md-12 mx-3 mt-3">
          <div class="iq-card ">
            <div class="iq-card-header pt-2">
              <div class="iq-card-title d-sm-block d-md-flex justify-content-between">
                <div class="d-block">
                  <h5 class="card-title">Bunker Summary</h5>
                  <p>Last Update {{ currAssetSummary.lastData }}</p>
                </div>

                <div class="d-sm-block d-md-flex">
                  <div class="mx-1">
                    <span class="d-block text-muted small text-left font-weight-bold mb-2">Bunker Unit</span>
                    <select name="" class="form-control" v-model="unit.bunker">
                      <option value="Mass">Mass</option>
                      <option value="Volume">Volume</option>
                    </select>
                  </div>
                  <div class="mx-1" v-if="unit.bunker === 'Mass'">
                    <span class="d-block text-muted small text-left font-weight-bold mb-2">Mass Unit</span>
                    <select name="" class="form-control" v-model="unit.mass">
                      <option value="mt">MT</option>
                      <option value="kg">KG</option>
                    </select>
                  </div>
                  <div class="mx-1" v-if="unit.bunker === 'Volume'">
                    <span class="d-block text-muted small text-left font-weight-bold mb-2">Volume Unit</span>
                    <select name="" class="form-control" v-model="unit.volume">
                      <option value="kl">KL</option>
                      <option value="l">L</option>
                    </select>
                  </div>
                  </div>
              </div>
            </div>
            <div class="iq-card-body">
              <div class="chart-row">
                <TotalBunkerChart :summaryData="currAsset.summary" :unit="unit" :mappedSeries="currAssetSummary.mappedSeries" />
                <BunkerFlowRateChart :summaryData="currAsset.summary" :unit="unit" :mappedSeries="currAssetSummary.mappedSeries" />
                <DensityChart :summaryData="currAsset.summary" :mappedSeries="currAssetSummary.mappedSeries" />
                <TemperatureChart :summaryData="currAsset.summary" :mappedSeries="currAssetSummary.mappedSeries" />
              </div>
              <BunkerSummaryChart v-if="currAssetSummary && currAssetSummary.mappedSeries" :mappedSeries="currAssetSummary.mappedSeries" />
            </div>
          </div>
        </div>

        <div class="col-md-12 mx-3 mt-3">
          <div class="iq-card">
            <div class="iq-card-header pt-2">
              <div class="iq-card-title d-sm-block d-md-flex justify-content-between align-items-start flex-column flex-md-row">
                <div class="d-block">
                  <h5 class="card-title">Datalog</h5>
                  <p>Last Update {{ currAssetSummary.lastData }}</p>
                </div>
                <button type="button" class="btn btn-success mt-2 mt-md-0" @click="onDownloadExcel">Download
                  Excel</button>
              </div>
            </div>

            <div class="iq-card-body pt-2 pb-5 mb-5">
              <div>
                <div style="height: 70vh; overflow-y: scroll;">
                  <table class="table table-striped table-sm table-hover table-bordered text-center">
                    <thead class="thead-light">
                      <tr>
                        <th v-for="(col, index) in columns" :key="col.key" :style="{ minWidth: col.minWidth + 'px', }" draggable="true" @dragstart="onDragStart(index)" @dragover.prevent="onDragOver(index)" @drop="onDrop(index)" @dragend="onDragEnd" class="draggable-column" :class="index === 0 ? 'sticky-column' : ''">
                          {{ col.label }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="data in paginatedData" :key="data.timestamp">
                        <td v-for="col in columns" :key="col.key">
                          {{ data[col.key] }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <hr />
              <nav>
                <ul class="pagination">
                  <li class="page-item" :class="{ disabled: currentPage === 1 }">
                    <a class="page-link" href="#" @click.prevent="changePage(currentPage - 1)">&laquo;</a>
                  </li>
                  <li class="page-item" :class="{ active: n === currentPage }" v-for="n in totalPages" :key="n">
                    <a class="page-link" href="#" @click.prevent="changePage(n)">{{ n }}</a>
                  </li>
                  <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                    <a class="page-link" href="#" @click.prevent="changePage(currentPage + 1)">&raquo;</a>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </template>

  </div>
</template>

<script>
/* eslint-disable object-curly-newline */
import { mapState, mapActions } from 'vuex';
import moment from 'moment';
import { generateTimestampsInSeconds, formatDateTime } from '@/util/date';
import { initAssetSummary, applyAssetSummaryFilter } from '@/util/asset-summary-loader';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import DataFilter from '@/components/DataFilter';
import BunkerSummaryChart from '@/components/charts/BunkerSummaryChart';

// Import the new specialized components
import TotalBunkerChart from '@/components/charts/TotalBunkerChart';
import BunkerFlowRateChart from '@/components/charts/BunkerFlowRateChart';
import DensityChart from '@/components/charts/DensityChart';
import TemperatureChart from '@/components/charts/TemperatureChart';

export default {
  name: 'BunkerDashboard',
  components: {
    DataFilter,
    BunkerSummaryChart,
    // Register the new components
    TotalBunkerChart,
    BunkerFlowRateChart,
    DensityChart,
    TemperatureChart,
  },
  data() {
    return {
      zoom: 10,
      currentPage: 1,
      perPage: 500,
      datarange: {
        start: new Date(2020, 0, 6),
        end: new Date(2020, 0, 23),
      },
      masks: {
        input: 'YYYY-MM-DD HH:mm ',
      },
      isLoading: true,
      unit: {
        bunker: 'Volume',
        mass: 'mt',
        volume: 'kl',
      },
      defaultColumns: [
        { key: 'datetime', label: 'Date Time', field: 'datetime', minWidth: 200 },
        { key: 'mass_flowrate', label: 'Mass Flowrate (kg/hour)', field: 'mass_flowrate', minWidth: 150 },
        { key: 'volume_flowrate', label: 'Volume Flowrate (L/hour)', field: 'volume_flowrate', minWidth: 150 },
        { key: 'mass_total', label: 'Mass Total (kg)', field: 'mass_total', minWidth: 150 },
        { key: 'volume_total', label: 'Volume Total (L)', field: 'volume_total', minWidth: 150 },
        { key: 'density', label: 'Density (g/cm³)', field: 'density', minWidth: 150 },
        { key: 'temperature', label: 'Temperature (°C)', field: 'temperature', minWidth: 150 },
      ],
      columns: null,
      appliedDateRange: null,
      appliedInterval: null,
      appliedMassId: null,
    };
  },
  async mounted() {
    // Check if there are query params from bunkering log redirect
    if (this.$route.query.start && this.$route.query.end && this.$route.query.massId) {
      const startUnix = parseInt(this.$route.query.start, 10);
      const endUnix = parseInt(this.$route.query.end, 10);
      const massId = this.$route.query.massId;
      const interval = this.$route.query.interval || 'minute';

      console.log('[BunkerDashboard] Redirect from bunkering log:', {
        startUnix,
        endUnix,
        massId,
        interval,
        startDate: moment.unix(startUnix).format('YYYY-MM-DD HH:mm:ss'),
        endDate: moment.unix(endUnix).format('YYYY-MM-DD HH:mm:ss'),
      });

      // Set date range from query params
      const startDate = moment.unix(startUnix);
      const endDate = moment.unix(endUnix);
      this.$store.commit('filter/SET_DATE', {
        start: startDate,
        end: endDate,
      });

      // Set massId from query params
      await this.$store.dispatch('filter/setCurrentMassId', massId);
      await this.$store.dispatch('filter/setInterval', interval);

      // Clear query params
      this.$router.replace({ query: {} });

      console.log('[BunkerDashboard] Store updated, date:', {
        start: this.$store.getters['filter/date'].start.format('YYYY-MM-DD HH:mm:ss'),
        end: this.$store.getters['filter/date'].end.format('YYYY-MM-DD HH:mm:ss'),
        massId: this.$store.getters['filter/currentMassId'],
      });

      // Load assets first before initAssetSummary
      console.log('[BunkerDashboard] Loading assets...');
      await this.$store.dispatch('asset/getAssetsData');
      console.log('[BunkerDashboard] Assets loaded:', this.$store.getters['asset/assets']?.length, 'assets');

      // Load devices
      await this.$store.dispatch('asset/getAssetsDevices');
      console.log('[BunkerDashboard] Devices loaded');

      // Now call initAssetSummary without withLastData to avoid GPS data fetch
      const date = this.$store.getters['filter/date'];
      console.log('[BunkerDashboard] Calling initAssetSummary with date:', {
        start: date.start.format('YYYY-MM-DD HH:mm:ss'),
        end: date.end.format('YYYY-MM-DD HH:mm:ss'),
      });
      await initAssetSummary(this.$store, {
        dateRange: date,
        interval,
        withDevices: false, // Already loaded
        withLastData: false, // Skip GPS data fetch
      });
      console.log('[BunkerDashboard] initAssetSummary completed');
      this.appliedDateRange = {
        start: new Date(moment(date.start).valueOf()),
        end: new Date(moment(date.end).valueOf()),
      };
      this.appliedInterval = this.$store.getters['filter/interval'];
      this.appliedMassId = this.$store.getters['filter/currentMassId'];

      console.log('[BunkerDashboard] Final state:', {
        appliedDateRange: this.appliedDateRange,
        appliedMassId: this.appliedMassId,
        currAsset: this.currAsset?.massName,
        currAssetSummary: this.currAssetSummary ? 'loaded' : 'null',
      });
    } else {
      // Normal page load (not from redirect)
      const date = this.$store.getters['filter/date'];
      await initAssetSummary(this.$store, {
        dateRange: date,
        withDevices: true,
        withLastData: true,
      });
      this.appliedDateRange = {
        start: new Date(moment(date.start).valueOf()),
        end: new Date(moment(date.end).valueOf()),
      };
      this.appliedInterval = this.$store.getters['filter/interval'];
      this.appliedMassId = this.$store.getters['filter/currentMassId'];
    }

    // Common code for both redirect and normal load
    this.loadColumnsFromIndexedDB()
      .then((stored) => {
        this.columns = stored || JSON.parse(JSON.stringify(this.defaultColumns));
      })
      .catch((err) => {
        console.warn('Could not load columns from IndexedDB:', err.message);
        this.columns = JSON.parse(JSON.stringify(this.defaultColumns));
      });

    this.isLoading = false;
  },
  computed: {
    ...mapState('asset', ['assets']),
    ...mapState('filter', {
      currentMassId: (state) => state.currentMassId,
      interval: (state) => state.interval,
    }),
    totalPages() {
      if (!this.currAssetSummary || !this.currAssetSummary.datalogs) return 1;
      return Math.ceil(this.currAssetSummary.datalogs.length / this.perPage);
    },
    paginatedData() {
      if (!this.currAssetSummary || !this.currAssetSummary.datalogs) return [];
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.currAssetSummary.datalogs.slice(start, end);
    },
    currAsset() {
      const currMassId = this.$store.getters['filter/currentMassId'];
      const assets = this.$store.getters['asset/assets'];
      if (!assets) {
        console.log('[currAsset] No assets available');
        return null;
      }

      const currMassIdStr = String(currMassId);
      const asset = assets.find((asset) => String(asset.massId) === currMassIdStr);
      console.log('[currAsset] Found asset:', asset?.massName, 'massId:', currMassId);
      return asset;
    },
    currAssetSummary() {
      const summary = this.currAsset?.summary || null;
      if (!summary) {
        console.log('[currAssetSummary] No summary available');
        return null;
      }

      console.log('[currAssetSummary] Summary available, processing...', {
        flowmeterKeys: Object.keys(summary.flowmeter?.data || {}),
        flowmeterDataCount: Object.keys(summary.flowmeter?.data || {}).length,
      });

      const mappedSummary = {};

      const effectiveDateRange = this.appliedDateRange || this.dateRange;
      const effectiveInterval = this.appliedInterval || this.interval;
      console.log('[currAssetSummary] Using date range:', {
        start: moment(effectiveDateRange.start).format('YYYY-MM-DD HH:mm:ss'),
        end: moment(effectiveDateRange.end).format('YYYY-MM-DD HH:mm:ss'),
        interval: effectiveInterval,
      });
      const timestamps = generateTimestampsInSeconds(
        moment(effectiveDateRange.start).unix(),
        moment(effectiveDateRange.end).unix(),
        effectiveInterval === 'minute' ? 60 : 3600,
      );

      console.log('[currAssetSummary] Generated timestamps count:', timestamps.length);

      const lastBunkerFmData = Object.values(summary?.flowmeter?.data || []).length > 0
        ? Object.values(summary.flowmeter.data)[Object.values(summary.flowmeter.data).length - 1]?.BUNKER_IN
        : {};

      mappedSummary.lastData = lastBunkerFmData ? formatDateTime(lastBunkerFmData.timestamp * 1000) : '-';

      const roundedFm = {};
      Object.keys(summary.flowmeter?.data || {}).forEach((key) => {
        const intervalSeconds = {
          hour: 3600,
          day: 86400,
          week: 604800,
        }[effectiveInterval] || 60;
        const roundedKey = Math.round(key / intervalSeconds) * intervalSeconds;
        roundedFm[roundedKey] = summary.flowmeter?.data[key];
      });

      console.log('[currAssetSummary] Rounded FM keys count:', Object.keys(roundedFm).length);

      const mappedSeries = timestamps.map((timestamp) => {
        const fmData = roundedFm[timestamp] || {};
        return {
          timestamp,
          fmData,
        };
      });

      mappedSummary.mappedSeries = mappedSeries;

      mappedSummary.datalogs = mappedSeries.map((data, i) => {
        const raw = data?.fmData?.BUNKER_IN || {};
        const { massTotal, massFlowrate, volumeTotal, volumeFlowrate, density, temperature } = raw;

        const formatValue = (value, unitType, conversionFactor = 0.001) => {
          if (value == null) return '-';
          const isMetric = (unitType === 'mass' && this.unit.mass === 'mt') || (unitType === 'volume' && this.unit.volume === 'kl');
          return isMetric ? (value * conversionFactor).toFixed(2) : value.toFixed(2);
        };

        return {
          id: i,
          datetime: data?.timestamp ? formatDateTime(data?.timestamp * 1000) : '-',
          mass_total: formatValue(massTotal, 'mass'),
          mass_flowrate: formatValue(massFlowrate, 'mass'),
          volume_total: formatValue(volumeTotal, 'volume'),
          volume_flowrate: formatValue(volumeFlowrate, 'volume'),
          density: density != null ? density.toFixed(2) : '-',
          temperature: temperature != null ? temperature.toFixed(2) : '-',
        };
      });

      return mappedSummary;
    },
    dateRange: {
      get() {
        const { start, end } = this.$store.getters['filter/date'];
        const startDate = new Date(moment(start).valueOf());
        const endDate = new Date(moment(end).valueOf());

        return { start: startDate, end: endDate };
      },
      set(val) {
        const start = moment(val.start);
        const end = moment(val.end);

        this.$store.commit('filter/SET_DATE', { start, end });
      },
    },
    selectedInterval: {
      get() {
        return this.interval;
      },
      set(val) {
        this.$store.dispatch('filter/setInterval', val);
      },
    },
    selectedAsset: {
      get() {
        return this.currentMassId;
      },
      set(val) {
        this.$store.dispatch('filter/setCurrentMassId', val);
      },
    },
  },
  methods: {
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
    ...mapActions(['updatekapalTrail']),
    async onApplyFilter() {
      this.isLoading = true;
      try {
        await applyAssetSummaryFilter(this.$store, {
          dateRange: this.dateRange,
          interval: this.selectedInterval || this.interval,
          massId: this.selectedAsset || this.currentMassId,
        });
        this.appliedDateRange = {
          start: new Date(moment(this.dateRange.start).valueOf()),
          end: new Date(moment(this.dateRange.end).valueOf()),
        };
        this.appliedInterval = this.interval;
        this.appliedMassId = this.currentMassId;
      } catch (err) {
        console.error(err);
      } finally {
        this.isLoading = false;
      }
    },
    async onDownloadExcel() {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Asset Summary');
      worksheet.columns = this.columns.map((col) => ({
        header: col.label,
        key: col.key,
        width: col.minWidth / 10,
      }));

      this.currAssetSummary.datalogs.forEach((row) => {
        worksheet.addRow(row); // Datalogs already formatted
      });

      const appliedRange = this.appliedDateRange || this.dateRange;
      const filename = `${this.currAsset?.massName} - ${formatDateTime(appliedRange.start, 'DD-MM-YYYY HH:mm')} - ${formatDateTime(appliedRange.end, 'DD-MM-YYYY HH:mm')}`;
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, `${filename}.xlsx`);
    },
    onDragStart(index) {
      this.dragSrcIndex = index;
    },

    // eslint-disable-next-line no-unused-vars
    onDragOver(index) {
      //
    },
    onDrop(index) {
      if (this.dragSrcIndex === null || this.dragSrcIndex === index) return;
      if (this.dragSrcIndex === 0 || index === 0) return;

      const draggedCol = this.columns.splice(this.dragSrcIndex, 1)[0];
      this.columns.splice(index, 0, draggedCol);
      this.dragSrcIndex = null;

      this.saveColumns(this.columns);
    },
    onDragEnd() {
      this.dragSrcIndex = null;
    },
    openIndexedDB() {
      return new Promise((resolve, reject) => {
        const request = indexedDB.open('mantraz');
        request.onupgradeneeded = (event) => {
          const db = event.target.result;
          if (!db.objectStoreNames.contains('columns')) {
            db.createObjectStore('columns', { keyPath: 'id' });
          }
        };
        request.onsuccess = (event) => resolve(event.target.result);
        request.onerror = (event) => reject(new Error(`IndexedDB error: ${event.target.errorCode}`));
      });
    },
    saveColumns(columns) {
      this.openIndexedDB().then((db) => {
        const tx = db.transaction('columns', 'readwrite');
        tx.objectStore('columns').put({ id: 2, columns });
      }).catch((err) => console.error('Failed to save columns:', err.message));
    },
    loadColumnsFromIndexedDB() {
      return this.openIndexedDB().then((db) => new Promise((resolve, reject) => {
        const request = db.transaction('columns', 'readonly').objectStore('columns').get(2);
        request.onsuccess = (event) => resolve(event.target.result?.columns || null);
        request.onerror = (event) => reject(new Error(`Failed to load columns: ${event.target.errorCode}`));
      }));
    },
    resetColumns() {
      this.columns = JSON.parse(JSON.stringify(this.defaultColumns));
    },
  },
  watch: {
    unit: {
      handler(newVal) {
        this.columns = this.columns.map((col) => {
          const newCol = { ...col };
          if (newVal.mass) {
            if (newCol.key === 'mass_flowrate') newCol.label = `Mass Flowrate (${newVal.mass.toUpperCase()}/Hour)`;
            if (newCol.key === 'mass_total') newCol.label = `Mass Total (${newVal.mass.toUpperCase()})`;
          }
          if (newVal.volume) {
            if (newCol.key === 'volume_flowrate') newCol.label = `Volume Flowrate (${newVal.volume.toUpperCase()}/Hour)`;
            if (newCol.key === 'volume_total') newCol.label = `Volume Total (${newVal.volume.toUpperCase()})`;
          }
          return newCol;
        });
        this.saveColumns(this.columns);
      },
      deep: true,
    },
    currentMassId(newVal) {
      console.log('[BunkerDashboard] currentMassId changed to:', newVal);
    },
  },
};
</script>

<style>
.chart-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  grid-gap: 5px;
  margin-top: 10px;
}

@media (max-width: 767.98px) {
  .chart-row {
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 5px;
    margin-top: 10px;
  }
}

.chart-title {
  font-size: 1.2em;
  font-weight: 500;
  line-height: 3em;
}
</style>
